import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import type { NavItem } from "@/app-shell/use-nav-items";
import type { SettingsNav } from "@/app-shell/use-settings-nav";
import { isHelpPath } from "@/help/routes";
import { CommandMenuDialog } from "./command-menu-dialog";
import { COMMAND_ROOT, popCommandPage, type CommandPage } from "./pages";

interface CommandMenuContextValue {
  setOpen: (open: boolean) => void;
  opensOnHelp: boolean;
}

const CommandMenuContext = createContext<CommandMenuContextValue | null>(null);

export const useCommandMenu = () => {
  const context = useContext(CommandMenuContext);
  if (!context) {
    throw new Error("useCommandMenu must be used within a CommandMenuProvider");
  }
  return context;
};

const isCommandShortcut = (event: KeyboardEvent) =>
  event.code === "KeyK" &&
  (event.metaKey || event.ctrlKey) &&
  !event.altKey &&
  !event.shiftKey &&
  !event.repeat;

interface CommandMenuProviderProps {
  items: NavItem[];
  settings: SettingsNav | null;
  showHelp?: boolean;
  onImpersonate?: () => void;
  children: ReactNode;
}

export const CommandMenuProvider = ({
  items,
  settings,
  showHelp = false,
  onImpersonate,
  children,
}: CommandMenuProviderProps) => {
  const [open, setOpenState] = useState(false);
  const [query, setQuery] = useState("");
  const [stack, setStack] = useState<CommandPage[]>([COMMAND_ROOT]);
  const { pathname } = useLocation();
  const inHelp = showHelp && isHelpPath(pathname);

  const reset = useCallback(() => {
    setQuery("");
    setStack(inHelp ? [COMMAND_ROOT, { kind: "help" }] : [COMMAND_ROOT]);
  }, [inHelp]);

  const setOpen = useCallback(
    (next: boolean) => {
      reset();
      setOpenState(next);
    },
    [reset],
  );

  const push = (page: CommandPage) => {
    setQuery("");
    setStack((current) => [...current, page]);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isCommandShortcut(event)) return;
      event.preventDefault();
      reset();
      setOpenState((current) => !current);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [reset]);

  return (
    <CommandMenuContext.Provider value={{ setOpen, opensOnHelp: inHelp }}>
      {children}
      <CommandMenuDialog
        open={open}
        query={query}
        stack={stack}
        items={items}
        settings={settings}
        showHelp={showHelp}
        onImpersonate={
          onImpersonate
            ? () => {
                setOpen(false);
                onImpersonate();
              }
            : undefined
        }
        onQueryChange={setQuery}
        onOpenChange={setOpen}
        onPush={push}
        onPop={() => {
          setQuery("");
          setStack((current) => popCommandPage(current));
        }}
        onJump={(index) => {
          setQuery("");
          setStack((current) => current.slice(0, index + 1));
        }}
      />
    </CommandMenuContext.Provider>
  );
};
